<template>
  <DashboardTopCardContent
    :callToActionButtonText="$t('L_view_plan')"
    @callToActionCallback="goToViewPlan"
  >
    <template #main-content>
      <div class="d-flex align-center justify-center">
        <h2 class="text-h2 font-weight-bold text-center">
          {{ $t('L_by_following_your_debt_stacking_plan') }}
          <v-tooltip
            :location="$vuetify.display.mdAndUp ? 'right' : 'top'"
            max-width="450"
            html="true"
            open-on-click
            close-delay="2000"
          >
            <template v-slot:activator="{ props }">
              <v-icon color="blue-darken-2" size="x-small" v-bind="props" :icon="mdiHelpCircle" />
            </template>
            <span v-html="$t('L_primerica_debt_stacking_methodology')"></span>
          </v-tooltip>
        </h2>
      </div>
      <div class="d-flex align-center justify-space-evenly">
        <div>
          <h6 class="text-center">{{ $t('L_debt_free_age') }}</h6>
          <p class="text-h1 text-center">{{ debtFreeAgeWStacking }}</p>
        </div>
        <div>
          <h6 class="text-center">{{ $t('L_years_sooner') }}</h6>
          <p class="text-h1 text-center">{{ yearsSooner }}</p>
        </div>
        <div>
          <h6 class="text-center">{{ $t('L_interest_avoided') }}</h6>
          <p class="text-h1 text-center">{{ interestAvoided }}</p>
        </div>
      </div>
    </template>
    <template #call-to-action>
      <a
        class="text-h4 cursor-pointer text-decoration-none font-weight-bold"
        @click="openImportantDisclosures"
      >
        {{ $t('L_important_assumption_and_disclosures') }}
      </a>
    </template>
  </DashboardTopCardContent>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { useDebtSolutionsStore } from '@/modules/debtsolutions/store/debtsolutions.store';
import { openInNewWindow } from '@/mixins/openNewWindow';
import { mdiHelpCircle } from '@mdi/js';
import DashboardTopCardContent from '../../DashboardTopCardContent.vue';
import { mapStores } from 'pinia';
import { RouteNames } from '@/router/routeNames';
import { $t } from '@/i18n/i18n.ts';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';

export default defineComponent({
  name: 'DebtSolutionsV03Top',
  components: { DashboardTopCardContent },
  data() {
    return {
      document: {
        title: '',
        content: '',
        loading: false,
      },
      mdiHelpCircle,
    };
  },
  mixins: [openInNewWindow],
  computed: {
    ...mapStores(useDebtSolutionsStore),
    debtFreeAgeWStacking() {
      return this.debtsolutionsStore.planWStacking?.planTotals?.primaryDebtPayoffAge ?? 0;
    },
    WOStackingMonth() {
      const debtPayoffYears =
        this.debtsolutionsStore.planWOStacking?.planTotals?.debtPayoffYears ?? 0;
      const debtPayoffMonths =
        this.debtsolutionsStore.planWOStacking?.planTotals?.debtPayoffMonths ?? 0;
      return 12 * debtPayoffYears + debtPayoffMonths;
    },
    WStackingMonth() {
      const debtPayoffYears =
        this.debtsolutionsStore.planWStacking?.planTotals?.debtPayoffYears ?? 0;
      const debtPayoffMonths =
        this.debtsolutionsStore.planWStacking?.planTotals?.debtPayoffMonths ?? 0;
      return 12 * debtPayoffYears + debtPayoffMonths;
    },
    yearsSooner() {
      const WOStackingMonth = this.WOStackingMonth || 0;
      const WStackingMonth = this.WStackingMonth || 0;
      const yearsSoonerVal = Math.ceil((WOStackingMonth - WStackingMonth) / 12);
      return Number.isNaN(yearsSoonerVal) ? '' : yearsSoonerVal;
    },
    interestAvoided(this: {
      debtsolutionsStore: ReturnType<typeof useDebtSolutionsStore>;
      $n: (value: number, format: string, locale: string) => string | null;
    }) {
      const WOStackingProjectedInterestPaid =
        this.debtsolutionsStore.planWOStacking?.planTotals?.projectedInterestPaid ?? 0;
      const WStackingProjectedInterestPaid =
        this.debtsolutionsStore.planWStacking?.planTotals?.projectedInterestPaid ?? 0;
      const diff = WOStackingProjectedInterestPaid - WStackingProjectedInterestPaid;
      return !Number.isNaN(diff) ? this.$n(diff, 'currencyNoCents', 'en-US') : '';
    },
  },
  methods: {
    $t,
    async goToViewPlan() {
      const route: RouteLocationNormalizedLoaded = this.$route;
      const router: Router = this.$router;

      if (route.path !== '/debtsolutions/myplan') {
        await router.push({ name: RouteNames.MY_PLAN });
      }
    },
    async openImportantDisclosures() {
      await this.openInNewWindow({
        url: `${window.appConfig.content_stack}/blt676ecb292039a611/MISC-006.pdf`,
        target: '_blank',
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.moveiconup {
  position: absolute;
  margin-top: -3px;
}
</style>
